import * as React from 'react'
import { cn } from '../../lib/utils'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'accent' | 'success'
  size?: 'sm' | 'md' | 'lg'
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, ...props }, ref) => {
    return (
      <button
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
          'disabled:pointer-events-none disabled:opacity-50 active:scale-95',
          variant === 'primary' && 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-soft hover:shadow-soft-lg hover:from-primary-600 hover:to-primary-700 focus-visible:ring-primary-500',
          variant === 'secondary' && 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm focus-visible:ring-primary-500',
          variant === 'outline' && 'border-2 border-primary-500 text-primary-600 dark:text-primary-400 hover:bg-primary-50/60 dark:hover:bg-primary-500/10 focus-visible:ring-primary-500',
          variant === 'ghost' && 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus-visible:ring-gray-500',
          variant === 'destructive' && 'bg-gradient-to-r from-error-500 to-error-600 text-white hover:from-error-600 hover:to-error-700 shadow-sm focus-visible:ring-error-500',
          variant === 'accent' && 'bg-gradient-to-r from-accent-500 to-accent-600 text-white shadow-soft hover:shadow-soft-lg hover:from-accent-600 hover:to-accent-700 focus-visible:ring-accent-500',
          variant === 'success' && 'bg-gradient-to-r from-success-500 to-success-600 text-white hover:from-success-600 hover:to-success-700 shadow-sm focus-visible:ring-success-500',
          size === 'sm' && 'h-9 px-4 text-sm',
          size === 'md' && 'h-11 px-6',
          size === 'lg' && 'h-13 px-8 text-lg',
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
